import { useEffect, useState } from "react";
import { X, Phone, MessageCircle, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../../lib/api";

export { fmtINR };

export const STAGES = [
  { k: "lead", label: "Lead", color: "bg-slate-100 text-slate-700 border-slate-200" },
  { k: "contacted", label: "Contacted", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { k: "interested", label: "Interested", color: "bg-violet-50 text-violet-700 border-violet-200" },
  { k: "follow-up", label: "Follow-up", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { k: "proposal", label: "Proposal", color: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  { k: "won", label: "Won", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { k: "lost", label: "Lost", color: "bg-red-50 text-red-600 border-red-200" },
];
export const DEAL_STAGES = ["open", "proposal", "won", "lost"];
export const SOURCES = ["Website", "Walk-in", "Referral", "Social Media", "WhatsApp", "Phone Call", "Other"];

export const stageMeta = (k) => STAGES.find((s) => s.k === k) || STAGES[0];
export const fmtDate = (d) => (d ? String(d).slice(0, 16).replace("T", " ") : "—");

export function StageBadge({ stage }) {
  const m = stageMeta(stage);
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide border ${m.color}`} data-testid={`stage-${stage}`}>{m.label}</span>;
}

export function FuBadge({ lead }) {
  if (!lead.follow_up_date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const d = String(lead.follow_up_date).slice(0, 10);
  if (lead.follow_up_done) return <span className="text-[10px] font-bold text-emerald-600">Done ✓</span>;
  const cls = d < today ? "bg-red-50 text-red-600 border-red-200" : d === today ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-muted text-muted-foreground border-border";
  const lbl = d < today ? "Overdue" : d === today ? "Today" : "Upcoming";
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-extrabold border ${cls}`} data-testid={`fu-${lead.id}`}>{lbl}: {fmtDate(lead.follow_up_date)}</span>;
}

export const inputCls = "w-full h-10 px-3 rounded-xl border border-input bg-background text-sm outline-none focus:ring-1 focus:ring-ring";
export const F = ({ label, children }) => (
  <div><p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">{label}</p>{children}</div>
);

export function Modal({ open, onClose, title, children, testid = "crm-modal" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-border shadow-2xl">
        <div className="sticky top-0 z-10 bg-background border-b border-border px-5 py-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid={`${testid}-close`} className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function QuickActions({ name, phone, whatsapp, email, storeName }) {
  const wa = String(whatsapp || phone || "").replace(/[^0-9]/g, "");
  const msg = encodeURIComponent(`Hi ${name || ""}, following up from ${storeName || "our team"} regarding your enquiry.`);
  const btn = "h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors";
  return (
    <div className="flex gap-1.5" data-testid="quick-actions">
      {phone && <a href={`tel:${phone}`} data-testid="qa-call" className={btn} title="Call"><Phone className="h-3.5 w-3.5" /></a>}
      {wa && <a href={`https://wa.me/${wa}?text=${msg}`} target="_blank" rel="noreferrer" data-testid="qa-whatsapp" className={`${btn} text-emerald-600`} title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></a>}
      {email && <a href={`mailto:${email}`} data-testid="qa-email" className={btn} title="Email"><Mail className="h-3.5 w-3.5" /></a>}
    </div>
  );
}

export function StatCard({ label, value, sub, tone = "", testid }) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4 min-w-0" data-testid={testid || `stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground truncate">{label}</p>
      <p className={`font-display font-extrabold text-2xl mt-1 tracking-tight ${tone}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

export function SaveBtn({ saving, label = "Save", testid = "crm-save" }) {
  return (
    <button type="submit" disabled={saving} data-testid={testid}
      className="w-full h-11 rounded-xl bg-foreground text-background font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
      {saving && <Loader2 className="h-4 w-4 animate-spin" />} {label}
    </button>
  );
}

/* ---------- Lead add/edit modal ---------- */
const blankLead = { name: "", company: "", phone: "", whatsapp: "", email: "", city: "", source: "Website",
  requirement: "", value: "", stage: "lead", assigned_to: "", follow_up_date: "", follow_up_note: "", reminder: "", note: "" };

export function LeadModal({ open, onClose, lead, onSaved }) {
  const [f, setF] = useState(blankLead);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(lead ? { ...blankLead, ...lead, value: lead.value || "", follow_up_date: (lead.follow_up_date || "").slice(0, 16), note: "" } : blankLead);
  }, [open, lead]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, value: parseFloat(f.value) || 0, follow_up_date: f.follow_up_date || null, reminder: f.reminder || null };
      if (lead?.id) await api.put(`/store-admin/crm/leads/${lead.id}`, body);
      else await api.post("/store-admin/crm/leads", body);
      toast.success(lead ? "Lead updated" : "Lead added");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={lead ? "Edit Lead" : "Add Lead"} testid="lead-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <F label="Full name *"><input required data-testid="lead-name" className={inputCls} value={f.name} onChange={set("name")} /></F>
          <F label="Company / Business"><input data-testid="lead-company" className={inputCls} value={f.company} onChange={set("company")} /></F>
          <F label="Mobile"><input data-testid="lead-phone" className={inputCls} value={f.phone} onChange={set("phone")} placeholder="919876543210" /></F>
          <F label="WhatsApp"><input data-testid="lead-whatsapp" className={inputCls} value={f.whatsapp} onChange={set("whatsapp")} placeholder="919876543210" /></F>
          <F label="Email"><input type="email" data-testid="lead-email" className={inputCls} value={f.email} onChange={set("email")} /></F>
          <F label="City"><input data-testid="lead-city" className={inputCls} value={f.city} onChange={set("city")} /></F>
          <F label="Source">
            <select data-testid="lead-source" className={inputCls} value={f.source} onChange={set("source")}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select>
          </F>
          <F label="Deal value (₹)"><input type="number" min="0" data-testid="lead-value" className={inputCls} value={f.value} onChange={set("value")} /></F>
          <F label="Stage">
            <select data-testid="lead-stage" className={inputCls} value={f.stage} onChange={set("stage")}>{STAGES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}</select>
          </F>
          <F label="Assigned user"><input data-testid="lead-assigned" className={inputCls} value={f.assigned_to} onChange={set("assigned_to")} /></F>
          <F label="Follow-up date"><input type="datetime-local" data-testid="lead-followup" className={inputCls} value={f.follow_up_date} onChange={set("follow_up_date")} /></F>
          <F label="Reminder"><input type="datetime-local" data-testid="lead-reminder" className={inputCls} value={f.reminder} onChange={set("reminder")} /></F>
        </div>
        <F label="Requirement"><textarea rows={2} data-testid="lead-requirement" className={`${inputCls} h-auto py-2`} value={f.requirement} onChange={set("requirement")} /></F>
        {!lead && <F label="Note"><textarea rows={2} data-testid="lead-note" className={`${inputCls} h-auto py-2`} value={f.note} onChange={set("note")} /></F>}
        <SaveBtn saving={saving} label={lead ? "Update Lead" : "Add Lead"} testid="lead-save" />
      </form>
    </Modal>
  );
}

/* ---------- Follow-up schedule modal ---------- */
export function FollowUpModal({ open, onClose, lead, onSaved }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setDate((lead?.follow_up_date || "").slice(0, 16)); setNote(lead?.follow_up_note || ""); } }, [open, lead]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/store-admin/crm/leads/${lead.id}/followup`, { date, note });
      toast.success("Follow-up scheduled");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={`Follow-up — ${lead?.name || ""}`} testid="followup-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Date & time *"><input required type="datetime-local" data-testid="fu-date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></F>
        <F label="Note"><textarea rows={2} data-testid="fu-note" className={`${inputCls} h-auto py-2`} value={note} onChange={(e) => setNote(e.target.value)} /></F>
        <SaveBtn saving={saving} label="Schedule Follow-up" testid="fu-save" />
      </form>
    </Modal>
  );
}

/* ---------- Quick note modal ---------- */
export function NoteModal({ open, onClose, title, onSave }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(text); toast.success("Note added"); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <Modal open={open} onClose={onClose} title={title || "Add Note"} testid="note-modal">
      <form onSubmit={submit} className="space-y-3">
        <textarea required rows={3} data-testid="note-text" className={`${inputCls} h-auto py-2`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note…" />
        <SaveBtn saving={saving} label="Add Note" testid="note-save" />
      </form>
    </Modal>
  );
}

/* ---------- Activity timeline (newest first) ---------- */
export function Timeline({ activities }) {
  if (!activities?.length) return <p className="text-sm text-muted-foreground" data-testid="timeline-empty">No activity yet.</p>;
  return (
    <div className="space-y-0" data-testid="activity-timeline">
      {activities.map((a) => (
        <div key={a.id} className="flex gap-3 pb-4 relative">
          <div className="flex flex-col items-center">
            <span className="h-2.5 w-2.5 rounded-full bg-foreground/70 mt-1.5 shrink-0" />
            <span className="flex-1 w-px bg-border" />
          </div>
          <div className="min-w-0 pb-1">
            <p className="text-sm font-semibold leading-snug break-words">{a.text}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{fmtDate(a.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
